import React from "react";
import PolicyPage from "./PolicyPage";
import { BUSINESS } from "@/mock";

export const ShippingPage = () => (
  <PolicyPage
    eyebrow="Help"
    title="Shipping & Delivery"
    updated="August 2026"
  >
    <h2>Where we ship</h2>
    <p>
      We ship <strong>pan-India</strong> to every serviceable pincode via trusted
      logistics partners (Delhivery, Blue Dart, and Xpressbees). If your pincode
      is not serviceable, our team will reach out to arrange an alternative.
    </p>

    <h2>Shipping charges</h2>
    <ul>
      <li>Orders <strong>above ₹2,999</strong>: Free shipping across India</li>
      <li>Orders below ₹2,999: Flat ₹149 shipping fee</li>
      <li>Large chandeliers may attract a handling fee shown clearly at checkout</li>
    </ul>

    <h2>Dispatch timelines</h2>
    <ul>
      <li>In-stock items: dispatched within <strong>48 working hours</strong></li>
      <li>Made-to-order chandeliers: 4–6 weeks lead time</li>
      <li>Orders placed on Sundays or public holidays are processed the next working day</li>
    </ul>

    <h2>Estimated delivery time</h2>
    <ul>
      <li>Pune, Mumbai, Bengaluru, Hyderabad: 2–4 working days</li>
      <li>Delhi NCR, Kolkata, Ahmedabad, Chennai: 3–5 working days</li>
      <li>Other cities: 5–8 working days</li>
      <li>Remote / North-East locations: 7–12 working days</li>
    </ul>

    <h2>Order tracking</h2>
    <p>
      Once your order is dispatched, you'll receive an SMS and email with a
      tracking link. You can also track your order by replying to your order
      confirmation email or by calling us at {BUSINESS.phone}.
    </p>

    <h2>Packaging</h2>
    <p>
      Every chandelier and light fixture is carefully packed in double-walled
      cartons with foam padding and shock-absorbers. Fragile items are marked
      and handled with special care during transit.
    </p>

    <h2>Delivery issues</h2>
    <p>
      If your parcel arrives damaged, please refuse delivery or take clear
      photos and email us at <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> within
      48 hours. We'll arrange a replacement or refund at no extra cost.
    </p>
  </PolicyPage>
);

export const ReturnsPage = () => (
  <PolicyPage
    eyebrow="Help"
    title="Returns & Refunds"
    updated="August 2026"
  >
    <h2>Our promise</h2>
    <p>
      We want you to love your Jhumar Collection lights. If something isn't
      right, you can start a return within <strong>7 days</strong> of delivery.
    </p>

    <h2>Eligible returns</h2>
    <ul>
      <li>Product arrived damaged, broken, or defective</li>
      <li>Wrong product delivered (different model, color, or size)</li>
      <li>Product doesn't match the description on the website</li>
      <li>You changed your mind (item must be unused and in original packaging)</li>
    </ul>

    <h2>Not eligible for return</h2>
    <ul>
      <li>Made-to-order or customized chandeliers</li>
      <li>Bulbs and consumable accessories once opened</li>
      <li>Items damaged due to improper installation or misuse</li>
      <li>Products returned after 7 days from delivery</li>
    </ul>

    <h2>How to start a return</h2>
    <ul>
      <li>Email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call {BUSINESS.phone} within 7 days of delivery</li>
      <li>Share your order number, reason for return, and 2–3 clear photos</li>
      <li>Our team confirms the return within 24 working hours and arranges a reverse pickup</li>
      <li>Pack the item in its original packaging with all accessories and the invoice</li>
    </ul>

    <h2>Refund timeline</h2>
    <ul>
      <li>We inspect the returned item within <strong>48 hours</strong> of receiving it</li>
      <li>Refunds are initiated within 3 working days after inspection</li>
      <li><strong>UPI / Cards:</strong> 5–7 working days to reflect in your account</li>
      <li><strong>Cash on Delivery:</strong> Refunded to a bank account you share — 3–5 working days</li>
    </ul>

    <h2>Exchanges</h2>
    <p>
      Prefer an exchange? We'll gladly swap for a different size, color, or
      product of equal value — subject to stock availability. Any price
      difference is billed or refunded accordingly.
    </p>
  </PolicyPage>
);

export const InstallationPage = () => (
  <PolicyPage
    eyebrow="Help"
    title="Installation Guide"
    updated="August 2026"
  >
    <h2>Before you install</h2>
    <ul>
      <li>Always switch off the main power at the MCB before starting work</li>
      <li>Use a certified electrician for chandeliers over 5 kg or 8+ bulbs</li>
      <li>Unbox on a soft surface; keep all screws and accessories together</li>
      <li>Check the ceiling type (concrete, false ceiling, wooden) before drilling</li>
    </ul>

    <h2>Tools you'll need</h2>
    <ul>
      <li>Insulated screwdriver set (Phillips + flat)</li>
      <li>Drill machine with 6 mm and 8 mm bits</li>
      <li>Ladder rated for the fixture height</li>
      <li>Wire stripper, insulation tape, and a tester</li>
    </ul>

    <h2>Step-by-step (chandeliers & pendants)</h2>
    <ul>
      <li><strong>Step 1:</strong> Turn off power at the MCB. Confirm with a tester.</li>
      <li><strong>Step 2:</strong> Fix the mounting bracket to the ceiling using the provided screws and wall plugs.</li>
      <li><strong>Step 3:</strong> Connect wires — Live (Red/Brown), Neutral (Black/Blue), Earth (Green/Yellow) — as per the wiring diagram in the box.</li>
      <li><strong>Step 4:</strong> Hang the fixture on the bracket hook. Adjust the drop height using the provided chain or cord.</li>
      <li><strong>Step 5:</strong> Install crystals or accessories one by one — handle with clean cotton gloves to avoid fingerprints.</li>
      <li><strong>Step 6:</strong> Screw in the bulbs (do not over-tighten). Turn power back on and test.</li>
    </ul>

    <h2>Wall lights & sconces</h2>
    <p>
      Wall lights follow the same wiring principles. Ensure the switch point
      is capped and insulated before mounting the backplate. Standard mounting
      height is 150–165 cm from the floor for reading sconces.
    </p>

    <h2>Safety tips</h2>
    <ul>
      <li>Never work on a live circuit — always double-check with a tester</li>
      <li>Match bulb wattage to the fixture's max rating (mentioned on the label)</li>
      <li>Clean crystals only with a soft microfiber cloth — no water or alcohol</li>
      <li>For ceilings above 12 ft, use scaffolding, not a step ladder</li>
    </ul>

    <h2>Need help?</h2>
    <p>
      Not comfortable installing yourself? Call us at {BUSINESS.phone} —
      we can recommend trusted electricians in Pune, Mumbai and nearby cities.
    </p>
  </PolicyPage>
);

export const WarrantyPage = () => (
  <PolicyPage
    eyebrow="Help"
    title="Warranty"
    updated="August 2026"
  >
    <h2>What's covered</h2>
    <p>
      Every Jhumar Collection product carries a <strong>12-month manufacturer
      warranty</strong> against manufacturing defects from the date of
      delivery.
    </p>
    <ul>
      <li>LED integrated fixtures: 24 months on the LED driver and module</li>
      <li>Crystal chandeliers: 12 months on the frame, wiring, and hardware</li>
      <li>Wall lights and sconces: 12 months on the body and wiring</li>
    </ul>

    <h2>What's not covered</h2>
    <ul>
      <li>Bulbs, tube lights, or consumable parts</li>
      <li>Damage from voltage fluctuation without a stabilizer</li>
      <li>Damage due to improper installation or third-party modifications</li>
      <li>Cosmetic wear-and-tear (fingerprints, minor scratches over time)</li>
      <li>Damage from water ingress on non-IP-rated indoor lights</li>
    </ul>

    <h2>How to raise a warranty claim</h2>
    <ul>
      <li>Email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> with your order number and photos/videos of the issue</li>
      <li>Our team responds within 24 working hours</li>
      <li>If covered under warranty, we send a replacement part or arrange a service visit (metro cities)</li>
      <li>Non-metro locations: we ship the replacement part with clear instructions</li>
    </ul>

    <h2>Extended care</h2>
    <p>
      Interested in an extended warranty for premium chandeliers? Contact our
      team at {BUSINESS.phone} and we'll share the AMC options available.
    </p>
  </PolicyPage>
);

export const PrivacyPage = () => (
  <PolicyPage
    eyebrow="Legal"
    title="Privacy Policy"
    updated="August 2026"
  >
    <h2>Who we are</h2>
    <p>
      This Privacy Policy explains how {BUSINESS.legalName} (“we”, “us”,
      “Jhumar Collection”) collects, uses and protects your personal
      information when you visit or purchase from our website.
    </p>

    <h2>What we collect</h2>
    <ul>
      <li>Name, delivery address, phone number, and email address</li>
      <li>Order history and payment reference IDs (we do not store card details)</li>
      <li>Device, browser, and cookie data for analytics and site improvement</li>
    </ul>

    <h2>How we use your information</h2>
    <ul>
      <li>To process and deliver your orders</li>
      <li>To send order updates and delivery notifications</li>
      <li>To respond to customer queries, warranty claims, and returns</li>
      <li>To share occasional updates about new collections (only if you opt in)</li>
    </ul>

    <h2>Payment security</h2>
    <p>
      All payments are processed through PCI-DSS compliant gateways. Card
      details are never stored on our servers. UPI and COD orders are
      verified independently before dispatch.
    </p>

    <h2>Sharing your data</h2>
    <p>
      We share only what's necessary with logistics partners for delivery and
      with payment processors for transaction settlement. We <strong>never
      sell or rent</strong> your data.
    </p>

    <h2>Your rights</h2>
    <ul>
      <li>Request a copy of the data we hold about you</li>
      <li>Correct or update your details anytime</li>
      <li>Ask us to delete your account and data (subject to GST record retention laws)</li>
      <li>Opt out of marketing emails via the unsubscribe link</li>
    </ul>

    <h2>Contact for privacy questions</h2>
    <p>
      Email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call
      {" "}{BUSINESS.phone}. Registered office: {BUSINESS.address.line1}, {BUSINESS.address.line2}, {BUSINESS.address.city} – {BUSINESS.address.pin}.
    </p>
  </PolicyPage>
);

export const TermsPage = () => (
  <PolicyPage
    eyebrow="Legal"
    title="Terms & Conditions"
    updated="August 2026"
  >
    <h2>Acceptance</h2>
    <p>
      By accessing jhumarcollection.com or placing an order, you agree to be
      bound by these Terms & Conditions. Please read them carefully.
    </p>

    <h2>Products & pricing</h2>
    <ul>
      <li>All prices are in Indian Rupees (₹) and include applicable GST</li>
      <li>We reserve the right to correct pricing errors before dispatch</li>
      <li>Product images are indicative; slight variation in crystal cut or brass tone is natural</li>
      <li>Stock availability is confirmed at the time of order acceptance</li>
    </ul>

    <h2>Orders</h2>
    <ul>
      <li>An order is confirmed only after we send you an order confirmation email</li>
      <li>We may cancel an order in case of stock, pricing, or address verification issues — you'll receive a full refund</li>
      <li>COD orders may require phone verification before dispatch</li>
    </ul>

    <h2>Payments</h2>
    <p>
      We accept UPI, credit/debit cards, netbanking, wallets, and Cash on
      Delivery. Payment must be completed before dispatch (except COD).
    </p>

    <h2>Intellectual property</h2>
    <p>
      All content on this site — photos, text, logos, product designs — is
      owned by {BUSINESS.legalName} or licensed to us. You may not reproduce,
      copy, or use our content without written permission.
    </p>

    <h2>Limitation of liability</h2>
    <p>
      Our liability for any product is limited to the price you paid for it.
      We are not responsible for damages arising from improper installation,
      misuse, or force majeure events.
    </p>

    <h2>Governing law</h2>
    <p>
      These terms are governed by the laws of India. Any disputes shall be
      subject to the exclusive jurisdiction of courts at Pune, Maharashtra.
    </p>

    <h2>Contact</h2>
    <p>
      Questions? Reach us at <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or
      {" "}{BUSINESS.phone}.
    </p>
  </PolicyPage>
);

export const RefundPolicyPage = () => (
  <PolicyPage
    eyebrow="Legal"
    title="Refund Policy"
    updated="August 2026"
  >
    <h2>Overview</h2>
    <p>
      This Refund Policy explains when and how you can get your money back for
      a Jhumar Collection order. For return eligibility details, please also
      read our <a href="/returns">Returns & Refunds</a> policy.
    </p>

    <h2>When you're eligible for a refund</h2>
    <ul>
      <li>Product arrived damaged or defective and reported within 48 hours</li>
      <li>Wrong product delivered</li>
      <li>Order cancelled by us due to stock or delivery issues</li>
      <li>Return approved under our 7-day return policy</li>
    </ul>

    <h2>Refund method</h2>
    <ul>
      <li><strong>UPI / Cards / Netbanking:</strong> Refund to the original payment method</li>
      <li><strong>Wallets:</strong> Refund to the same wallet</li>
      <li><strong>Cash on Delivery:</strong> Refund via NEFT/UPI to a bank account you share</li>
    </ul>

    <h2>Refund timeline</h2>
    <ul>
      <li>Refund initiated within 3 working days of return approval</li>
      <li>Bank credit within 5–7 working days after initiation</li>
      <li>You'll receive an SMS/email once the refund is processed</li>
    </ul>

    <h2>Partial refunds</h2>
    <p>
      In some cases (e.g. missing accessories, minor damage to packaging), we
      may offer a partial refund with your consent instead of a full return.
    </p>

    <h2>Non-refundable</h2>
    <ul>
      <li>Shipping fees on “change of mind” returns</li>
      <li>Made-to-order chandeliers once production has started</li>
      <li>Installation charges paid to third-party electricians</li>
    </ul>

    <h2>Need help?</h2>
    <p>
      Email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call
      {" "}{BUSINESS.phone} — our support team is available Mon–Sat, 10:30 am
      to 8:30 pm.
    </p>
  </PolicyPage>
);

export const CancellationPage = () => (
  <PolicyPage
    eyebrow="Legal"
    title="Cancellation Policy"
    updated="August 2026"
  >
    <h2>Overview</h2>
    <p>
      At Jhumar Collection we understand that plans can change. This policy
      explains when and how you can cancel your order, and what happens with
      your payment after cancellation.
    </p>

    <h2>Cancellation by the customer</h2>
    <h3>Before dispatch</h3>
    <ul>
      <li>You can cancel any in-stock order <strong>free of charge</strong> before it is dispatched from our warehouse.</li>
      <li>Cancellation window is usually <strong>within 12 hours</strong> of placing the order, as most orders are dispatched within 24–48 working hours.</li>
      <li>To cancel, email <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or call {BUSINESS.phone} with your order number.</li>
      <li>Once cancelled, a full refund is initiated within 3 working days.</li>
    </ul>

    <h3>After dispatch</h3>
    <ul>
      <li>Once the order is handed over to the courier, it can no longer be cancelled online.</li>
      <li>You may refuse delivery when the courier arrives — the parcel will be returned to us and refunded after inspection.</li>
      <li>A reverse-shipping fee (typically ₹149–₹499 depending on size) may be deducted from your refund.</li>
    </ul>

    <h3>Made-to-order & customised products</h3>
    <ul>
      <li>Made-to-order chandeliers and customised fixtures can be cancelled only <strong>within 24 hours</strong> of order confirmation.</li>
      <li>Once production has started, cancellation is not possible as materials are cut and reserved for your order.</li>
      <li>If production has already begun, a partial refund of up to 50% may be considered on a case-by-case basis.</li>
    </ul>

    <h2>Cancellation by Jhumar Collection</h2>
    <p>
      In rare cases, we may cancel an order from our end. If we do, you will
      receive a full refund and a clear reason. This can happen when:
    </p>
    <ul>
      <li>The product is out of stock and cannot be restocked in reasonable time</li>
      <li>The delivery pincode is not serviceable by our logistics partners</li>
      <li>There is a pricing error or a listing mistake on the website</li>
      <li>Payment verification fails or the order is flagged as high-risk / fraudulent</li>
      <li>COD verification calls remain unanswered after 2 attempts</li>
    </ul>

    <h2>How to cancel</h2>
    <ul>
      <li><strong>Email:</strong> <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> with subject "Cancel Order #[your order number]"</li>
      <li><strong>Phone:</strong> {BUSINESS.phone} (Mon–Sat, 10:30 am – 8:30 pm)</li>
      <li><strong>WhatsApp:</strong> Send your order number to {BUSINESS.phone}</li>
    </ul>

    <h2>Refunds after cancellation</h2>
    <ul>
      <li><strong>UPI / Cards / Netbanking:</strong> Refunded to the original payment method within 5–7 working days</li>
      <li><strong>Wallets:</strong> Refunded to the same wallet within 3–5 working days</li>
      <li><strong>Cash on Delivery:</strong> Refunded via NEFT/UPI to a bank account you share — 3–5 working days</li>
      <li>You will receive an SMS and email confirmation once the refund is initiated.</li>
    </ul>

    <h2>Partial cancellation</h2>
    <p>
      Ordered multiple items and want to cancel just one? No problem — reach
      out with your order number and the specific item you want to remove.
      We'll cancel that item and refund its value proportionally, provided the
      order has not yet been dispatched.
    </p>

    <h2>Need help?</h2>
    <p>
      Our support team is here Mon–Sat, 10:30 am – 8:30 pm. Reach us at{" "}
      <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a> or{" "}
      {BUSINESS.phone} and we'll help you cancel or modify your order quickly.
    </p>
  </PolicyPage>
);

